import { NavLink, useLocation } from "react-router-dom";
import { Home, Search, PlusSquare, User, LogOut, Zap, Compass, Wand2, MessageCircle, Bell, Coins, Trophy, Wallet as WalletIcon, BarChart3, Crown, ShoppingBag, Radio, Sparkles, GraduationCap } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { api, picUrl } from "@/lib/api";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 text-sm transition-colors group ${
    isActive
      ? "bg-white/5 text-[#39FF14] border-l-2 border-[#39FF14]"
      : "text-white/60 hover:text-white border-l-2 border-transparent"
  }`;

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { subscribe } = useWS();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [coins, setCoins] = useState(0);
  const [feedSpin, setFeedSpin] = useState(false);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setNotifUnread(0);
      setCoins(0);
      return;
    }
    const loadDms = () =>
      api
        .get("/messages/unread-count")
        .then((r) => setUnread(r.data.unread || 0))
        .catch(() => setUnread(0));
    const loadNotifs = () =>
      api
        .get("/notifications/unread-count")
        .then((r) => setNotifUnread(r.data.count || 0))
        .catch(() => setNotifUnread(0));
    const loadCoins = () =>
      api
        .get("/me/earnings")
        .then((r) => setCoins(r.data.coins_balance || 0))
        .catch(() => setCoins(0));
    loadDms();
    loadNotifs();
    loadCoins();
    // refresh in real-time on WS events; cheap server queries
    const unsub = subscribe((evt) => {
      if (evt.type === "message:new" || evt.type === "conversation:read") {
        loadDms();
      }
      if (evt.type === "notification_new") {
        setNotifUnread((n) => n + 1);
      }
      if (evt.type === "tip_received" && evt.amount_cents) {
        setCoins((c) => c + Math.floor(evt.amount_cents / 2));
      }
    });
    return () => unsub();
  }, [user, subscribe]);

  return (
    <aside
      data-testid="desktop-sidebar"
      className="hidden md:flex fixed left-0 top-0 bottom-0 w-60 bg-[#050505] border-r border-white/10 flex-col z-40"
    >
      <div className="px-5 py-7 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 bg-[#39FF14] flex items-center justify-center">
            <Zap className="h-4 w-4 text-black" strokeWidth={2.5} />
          </div>
          <span
            className="font-display font-black text-lg tracking-tight glitch neon-text-gold"
            data-text="MatrixShopping"
          >
            MatrixShopping
          </span>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30 mt-3">
          // v1.0
        </p>
      </div>

      <nav className="flex-1 py-4 flex flex-col gap-1">
        <NavLink
          to="/"
          end
          className={linkCls}
          data-testid="nav-feed"
          onClick={(e) => {
            // Reshuffle the algorithm when the user clicks "For You"
            // while already on the feed. See BottomNav for the mobile
            // equivalent — Feed.jsx listens for `feed:refresh`.
            if (location.pathname === "/") {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("feed:refresh"));
              setFeedSpin(true);
              window.setTimeout(() => setFeedSpin(false), 700);
            }
          }}
        >
          <Home
            className={`h-5 w-5 transition-transform duration-700 ${feedSpin ? "rotate-[360deg] text-[#39FF14] drop-shadow-[0_0_6px_rgba(57,255,20,0.9)]" : ""}`}
            strokeWidth={1.5}
            data-testid="nav-feed-icon"
          />
          <span className="font-medium">For You</span>
        </NavLink>
        <NavLink to="/discover" className={linkCls} data-testid="nav-discover">
          <Compass className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-medium">Discover</span>
        </NavLink>
        <NavLink to="/live" className={linkCls} data-testid="nav-live">
          <Radio className="h-5 w-5 text-[#FF007F]" strokeWidth={1.5} />
          <span className="font-medium">Live</span>
          <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-[#FF007F] text-white font-black rounded-sm">NEW</span>
        </NavLink>
        <NavLink to="/search" className={linkCls} data-testid="nav-search">
          <Search className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-medium">Search</span>
        </NavLink>
        <NavLink to="/upload" className={linkCls} data-testid="nav-upload">
          <PlusSquare className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-medium">Upload</span>
        </NavLink>
        <NavLink to="/generate" className={linkCls} data-testid="nav-generate">
          <Wand2 className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-medium">AI Studio</span>
        </NavLink>
        {user && (
          <NavLink to="/messages" className={linkCls} data-testid="nav-messages">
            <MessageCircle className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Inbox</span>
            {unread > 0 && (
              <span
                data-testid="sidebar-unread-badge"
                className="bg-[#FF007F] text-black text-[10px] font-bold px-2 py-0.5"
              >
                {unread}
              </span>
            )}
          </NavLink>
        )}
        {user && (
          <NavLink
            to="/notifications"
            className={linkCls}
            data-testid="nav-notifications"
            onClick={() => setNotifUnread(0)}
          >
            <Bell className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Notifications</span>
            {notifUnread > 0 && (
              <span
                data-testid="sidebar-notif-badge"
                className="bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(57,255,20,0.5)]"
              >
                {notifUnread}
              </span>
            )}
          </NavLink>
        )}
        {user && (
          <NavLink
            to="/wallet"
            className={linkCls}
            data-testid="nav-wallet"
          >
            <WalletIcon className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Wallet</span>
            {(user?.wallet_balance_cents || 0) > 0 && (
              <span
                data-testid="sidebar-wallet-badge"
                className="bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(57,255,20,0.5)]"
              >
                ${((user.wallet_balance_cents || 0) / 100).toFixed(0)}
              </span>
            )}
          </NavLink>
        )}
        {user && (
          <NavLink
            to="/earnings"
            className={linkCls}
            data-testid="nav-earnings"
          >
            <Coins className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Earnings</span>
            {coins > 0 && (
              <span
                data-testid="sidebar-coins-badge"
                className="bg-[#FF007F] text-white text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(255,0,127,0.5)]"
              >
                {coins > 999 ? `${(coins / 1000).toFixed(1)}k` : coins}
              </span>
            )}
          </NavLink>
        )}
        {user && (
          <NavLink
            to="/shop-analytics"
            className={linkCls}
            data-testid="nav-shop-analytics"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Shop Stats</span>
          </NavLink>
        )}
        <NavLink
          to="/top-sharers"
          className={linkCls}
          data-testid="nav-top-sharers"
        >
          <BarChart3 className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-medium flex-1">Top Sharers</span>
        </NavLink>
        {user && (
          <NavLink
            to="/pro"
            className={linkCls}
            data-testid="nav-pro"
          >
            <Crown className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium flex-1">Pro</span>
            {user?.is_pro ? (
              <span
                data-testid="sidebar-pro-badge"
                className="bg-[#B026FF] text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest shadow-[0_0_10px_rgba(176,38,255,0.5)]"
              >
                pro
              </span>
            ) : (
              <span
                data-testid="sidebar-pro-badge-upsell"
                className="font-mono text-[9px] uppercase tracking-widest text-[#B026FF]/80 border border-[#B026FF]/50 px-1.5 py-0.5"
              >
                new
              </span>
            )}
          </NavLink>
        )}
        {user && (
          <NavLink
            to="/my-boosts"
            className={linkCls}
            data-testid="nav-my-boosts"
          >
            <Trophy className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium">Trophy Shelf</span>
          </NavLink>
        )}
        {user && (
          <NavLink
            to={`/u/${user.username}`}
            className={linkCls}
            data-testid="nav-profile"
          >
            <User className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium">Profile</span>
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink
            to={`/admin/user/${user.username}/shares`}
            className={linkCls}
            data-testid="nav-admin-shares"
          >
            <BarChart3 className="h-5 w-5 text-[#00FFFF]" strokeWidth={1.5} />
            <span className="font-medium flex-1">Share Activity</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#00FFFF]/70 px-1.5 py-0.5 border border-[#00FFFF]/40">
              admin
            </span>
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink
            to="/admin/gifts"
            className={linkCls}
            data-testid="nav-admin-gifts"
          >
            <Sparkles className="h-5 w-5 text-[#FFC300]" strokeWidth={1.5} />
            <span className="font-medium flex-1">Boost Animations</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFC300]/60 px-1.5 py-0.5 border border-[#FFC300]/40">
              admin
            </span>
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink
            to="/admin/analytics"
            className={linkCls}
            data-testid="nav-admin-analytics"
          >
            <BarChart3 className="h-5 w-5 text-[#FF007F]" strokeWidth={1.5} />
            <span className="font-medium flex-1">Analytics</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#FF007F]/60 px-1.5 py-0.5 border border-[#FF007F]/40">
              admin
            </span>
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink
            to="/admin/guide-agent"
            className={linkCls}
            data-testid="nav-admin-guide-agent"
          >
            <GraduationCap className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
            <span className="font-medium flex-1">Guide Agent</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#39FF14]/60 px-1.5 py-0.5 border border-[#39FF14]/40">
              admin
            </span>
          </NavLink>
        )}
      </nav>

      <div className="border-t border-white/10 p-4">
        {user ? (
          <div className="space-y-3">
            <div
              className="flex items-center gap-3"
              data-testid="sidebar-user-block"
            >
              {user.picture ? (
                <img
                  src={picUrl(user.picture)}
                  alt=""
                  className="h-9 w-9 object-cover border border-[#39FF14]/40"
                />
              ) : (
                <div className="h-9 w-9 bg-[#FF007F]/20 flex items-center justify-center font-display font-bold text-[#FF007F]">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{user.name}</p>
                <p className="font-mono text-[10px] text-white/40 truncate">
                  @{user.username}
                </p>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs uppercase tracking-wider text-white/60 hover:text-[#FF007F] border border-white/10 hover:border-[#FF007F]/50 transition-all"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              Sign out
            </button>
            <div className="flex items-center justify-center gap-3 pt-1 font-mono text-[9px] uppercase tracking-widest text-white/30">
              <NavLink to="/terms" data-testid="sidebar-terms-link" className="hover:text-white/60">terms</NavLink>
              <span>·</span>
              <NavLink to="/privacy" data-testid="sidebar-privacy-link" className="hover:text-white/60">privacy</NavLink>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <NavLink
              to="/login"
              data-testid="sidebar-login-btn"
              className="block text-center w-full bg-[#39FF14] text-black font-bold uppercase tracking-wider py-3 text-sm hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all"
            >
              Log in
            </NavLink>
            <div className="flex items-center justify-center gap-3 pt-1 font-mono text-[9px] uppercase tracking-widest text-white/30">
              <NavLink to="/terms" data-testid="sidebar-terms-link" className="hover:text-white/60">terms</NavLink>
              <span>·</span>
              <NavLink to="/privacy" data-testid="sidebar-privacy-link" className="hover:text-white/60">privacy</NavLink>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
